/**
 * Global reading-mode store.
 *
 * Lets visitors switch the whole site into a calmer, easier-to-read mode that
 * enlarges body text, relaxes line-height/letter-spacing and widens the reading
 * measure on long-form pages. Reading mode also forces reduced motion so the
 * experience stays distraction-free.
 *
 * The chosen preference is persisted to localStorage and reflected on <html>
 * via the `.reading-mode` class consumed by index.css.
 */

const STORAGE_KEY = "sto-reading-mode";
const listeners = new Set<() => void>();

let enabled = false;

function readStored(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return localStorage.getItem(STORAGE_KEY) === "on";
  } catch {
    return false;
  }
}

export function isReadingMode(): boolean {
  return enabled;
}

function apply(): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.classList.toggle("reading-mode", enabled);
  // Reading mode implies a calmer, reduced-motion experience.
  root.classList.toggle("reading-reduce-motion", enabled);
}

function emit(): void {
  listeners.forEach((l) => l());
}

export function setReadingMode(next: boolean): void {
  enabled = next;
  try {
    if (next) localStorage.setItem(STORAGE_KEY, "on");
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore persistence errors (private mode, etc.) */
  }
  apply();
  emit();
}

export function toggleReadingMode(): void {
  setReadingMode(!enabled);
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

let initialised = false;

/** Call once at startup to hydrate the preference. */
export function initReadingMode(): void {
  if (initialised || typeof window === "undefined") return;
  initialised = true;
  enabled = readStored();
  apply();
}
